import { Text } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { Dimensions, StyleSheet, View } from "react-native";
import Svg, { Circle, Text as SvgText } from "react-native-svg";

export interface MenstrualCycleDiagramProps {
  numberOfDaysInCycle: number;
  numberOfMenstruationDays: number;
  currentDayInCycle: number;
  daysUntilNextPeriod: number;
}

const MenstrualCycleDiagram: React.FC<MenstrualCycleDiagramProps> = ({
  numberOfDaysInCycle = 28,
  numberOfMenstruationDays = 5,
  currentDayInCycle = 1,
  daysUntilNextPeriod = null,
}) => {
  const { t } = useTranslation();

  const { width } = Dimensions.get("window");
  const size = width * 0.7; // 80% of screen width
  const strokeWidth = size / 20; // Thinner stroke for a cleaner look
  const radius = size / 2 - strokeWidth / 2; // Ensure the radius accounts for stroke width
  const circleCircumference = 2 * Math.PI * radius;
  const notchPosition = 270; // Position in degrees
  const notchSize = 0.05 * circleCircumference; // Size of the notch

  // Smaller indicator for menstruation start
  const menstruationCircleSize = strokeWidth / 3;
  const menstruationCirclePosition = 0; // Position in degrees

  // Calculate the position of the menstruation circle
  const menstruationCircleX = size / 2 + radius * Math.cos((menstruationCirclePosition - 90) * (Math.PI / 180));
  const menstruationCircleY = size / 2 + radius * Math.sin((menstruationCirclePosition - 90) * (Math.PI / 180));

  // Calculate the size and offset of the menstruation segment
  const menstruationSegmentSize = (numberOfMenstruationDays / numberOfDaysInCycle) * circleCircumference;
  const menstruationSegmentOffset = -circleCircumference * ((menstruationCirclePosition - 90) / 360);

  // Calculate the position of the fertile window circle and segment
  const numberOfFertileWindowDays = 6;
  const startDayOfFertileWindow = 8;
  const fertileWindowStartAngle = (startDayOfFertileWindow * 360) / numberOfDaysInCycle - 90;
  const fertileWindowCircleOffset = -circleCircumference * (fertileWindowStartAngle / 360);
  const fertileWindowSegmentSize = (numberOfFertileWindowDays / numberOfDaysInCycle) * circleCircumference;

  // Calculate the position of the ovulation circle
  const ovulationDay = 13;
  const ovulationCircleSize = strokeWidth / 3; // Smaller ovulation indicator
  const ovulationCirclePosition = (ovulationDay * 360) / numberOfDaysInCycle - 90;

  const ovulationCircleX = size / 2 + radius * Math.cos(ovulationCirclePosition * (Math.PI / 180));
  const ovulationCircleY = size / 2 + radius * Math.sin(ovulationCirclePosition * (Math.PI / 180));

  // Calculate the position of the current day of cycle circle
  const currentDayOfCycleAngle = currentDayInCycle * (360 / numberOfDaysInCycle);
  const currentDayOfCycleCircleSize = strokeWidth * 1.5; // Slightly larger for better visibility

  const currentDayOfCycleCircleX = size / 2 + radius * Math.cos((currentDayOfCycleAngle - 90) * (Math.PI / 180));
  const currentDayOfCycleCircleY = size / 2 + radius * Math.sin((currentDayOfCycleAngle - 90) * (Math.PI / 180));

  // Function to generate day markers around the circle
  const generateDayMarkers = (): JSX.Element[] => {
    const markers = [];
    for (let i = 1; i <= numberOfDaysInCycle; i += 1) {
      const angle = i * (360 / numberOfDaysInCycle) - 90;
      const markerX = size / 2 + (radius + strokeWidth / 2 + 5) * Math.cos(angle * (Math.PI / 180));
      const markerY = size / 2 + (radius + strokeWidth / 2 + 5) * Math.sin(angle * (Math.PI / 180));

      markers.push(<Circle key={`marker-${i}`} cx={markerX} cy={markerY} r={1.5} fill="#d0d0d0" />);
    }
    return markers;
  };

  return (
    <View style={styles.chartContainer}>
      <Svg width={size + currentDayOfCycleCircleSize} height={size + currentDayOfCycleCircleSize}>
        {/* Base circle - lighter gray */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e0e0e0" // Lighter grey color
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={`${circleCircumference - notchSize}, ${notchSize}`}
          strokeDashoffset={-circleCircumference * (notchPosition / 360)}
          strokeLinecap="round"
        />

        {/* Day markers around the circle */}
        {generateDayMarkers()}

        {/* Menstruation Segment - lighter pink */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#ffdddd" // Lighter pink color
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={`${menstruationSegmentSize}, ${circleCircumference - menstruationSegmentSize}`}
          strokeDashoffset={menstruationSegmentOffset}
          strokeLinecap="round"
        />

        {/* Menstruation Start Circle - red dot */}
        <Circle
          cx={menstruationCircleX}
          cy={menstruationCircleY}
          r={menstruationCircleSize}
          fill="#ff6666" // Lighter red
        />

        {/* Fertile Window Segment - lighter blue */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#d0d0ff" // Lighter blue
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={`${fertileWindowSegmentSize}, ${circleCircumference - fertileWindowSegmentSize}`}
          strokeDashoffset={fertileWindowCircleOffset}
          strokeLinecap="round"
        />

        {/* Fertile day indicator - blue droplet */}
        <Circle cx={ovulationCircleX} cy={ovulationCircleY} r={ovulationCircleSize * 1.5} fill="#80a0ff" />

        {/* Current Day of Cycle Circle - clean white circle with subtle border */}
        <Circle
          cx={currentDayOfCycleCircleX}
          cy={currentDayOfCycleCircleY}
          r={currentDayOfCycleCircleSize}
          fill="white"
          stroke="#e0e0e0"
          strokeWidth={1}
        />

        {/* Day number - centered and clean */}
        <SvgText
          x={currentDayOfCycleCircleX}
          y={currentDayOfCycleCircleY + 5} // Adjusted for better vertical alignment
          fontSize={16}
          fontWeight="bold"
          fill="#333333"
          textAnchor="middle"
          alignmentBaseline="middle"
        >
          {currentDayInCycle}
        </SvgText>
      </Svg>

      {/* Text inside the donut - simplified to match Clue */}
      <View style={styles.textContainer}>
        {daysUntilNextPeriod !== null && daysUntilNextPeriod !== undefined && (
          <Text style={styles.daysUntilText}>
            {daysUntilNextPeriod}{" "}
            {t("onboarding.onboarding_menstrual_cycle_details_screen.days_until_your_next_period")}
          </Text>
        )}

        {/* NOTE: We do not currently shown this */}
        {/* Potential fertile day text
        {currentDayInCycle >= startDayOfFertileWindow &&
          currentDayInCycle <= startDayOfFertileWindow + numberOfFertileWindowDays && (
            <Text style={styles.fertileText}>{t("Potential fertile day")}</Text>
          )} */}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  chartContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 40,
  },
  textContainer: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    width: "80%",
  },
  daysUntilText: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 8,
  },
  fertileText: {
    fontSize: 14,
    color: "#80a0ff",
    textAlign: "center",
  },
});

export default MenstrualCycleDiagram;
